import React, { ReactElement, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import AppModal from "components/shared/modal/AppModal";

import { postEditBaremetalFirewall } from "redux/handlers/baremetalHandle";

import { useAppDispatch } from "hooks/reduxHook";

import { IFirewallBarmetalData, IFirewallRuleData } from "types/api/baremetalApiInterface";

import EditingRule from "../components/EditingRule";
import RuleToEdit from "../components/RuleToEdit";

const newRuleData = {
 ip_version: "ipv4",
 name: "",
 dst_ip: "",
 src_ip: "",
 dst_port: 1,
 src_port: 1,
 protocol: "tcp",
 tcp_flags: "",
 action: "accept"
};

const EditFirewallConfiguration = ({
 firewall,
 closeEdit
}: {
 firewall: IFirewallBarmetalData;
 closeEdit: () => void;
}): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const [enableFirewall, setEnableFirewall] = useState<boolean>(false);
 const [enableHos, setEnableHos] = useState<boolean>(false);
 const [inputRules, setInputRules] = useState<Array<IFirewallRuleData>>([]);
 const [outputRules, setOutputRules] = useState<Array<IFirewallRuleData>>([]);
 const [loadingData, setLoadingData] = useState<boolean>(true);
 const [ruleToEdit, setRuleToEdit] = useState<number>(-1);
 const [outputRuleToEdit, setOutputRuleToEdit] = useState<number>(-1);
 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);

 const handleOpen = () => setOpen(true);
 const handleClose = () => setOpen(false);

 const handleLoadData = () => {
  if (firewall) {
   setEnableFirewall(firewall?.status === "active");
   setEnableHos(firewall?.whitelist_hos);
   setInputRules(firewall.input_rules);
   setOutputRules(firewall.output_rules);
  }
  setLoadingData(false);
 };

 useEffect(() => {
  firewall && handleLoadData();
 }, [firewall]);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postEditBaremetalFirewall(firewall.barmetal_id_db, enableHos, inputRules, outputRules)
  );
  setLoading(false);
  closeEdit();
 };

 const selectRuleToEdit = (type: string, index: number) => {
  if (type === "input") {
   setRuleToEdit(index);
  } else {
   setOutputRuleToEdit(index);
  }
 };

 const handleAddRule = (type: string) => {
  if (type === "input") {
   setInputRules([...inputRules, newRuleData]);
   setRuleToEdit(inputRules.length);
  } else {
   setOutputRules([...outputRules, newRuleData]);
   setOutputRuleToEdit(outputRules.length);
  }
 };

 const handleAddEditedRule = (type: string, newRule: IFirewallRuleData) => {
  if (type === "input") {
   setInputRules(
    inputRules.map((element, index) => {
     if (index === ruleToEdit) {
      return newRule;
     } else {
      return element;
     }
    })
   );
   setRuleToEdit(-1);
  } else {
   setOutputRules(
    outputRules.map((element, index) => {
     if (index === ruleToEdit) {
      return newRule;
     } else {
      return element;
     }
    })
   );
   setOutputRuleToEdit(-1);
  }
 };

 const handleDeleteRule = (type: string) => {
  if (type === "input") {
   setInputRules(inputRules.filter((_element, index) => index !== ruleToEdit));
   setRuleToEdit(-1);
  } else {
   setOutputRules(outputRules.filter((_element, index) => index !== ruleToEdit));
   setOutputRuleToEdit(-1);
  }
 };

 return loadingData ? (
  <></>
 ) : (
  <Stack spacing={2}>
   <Stack>
    <Stack direction="row" spacing={1} alignItems="center">
     <Typography variant="caption" fontWeight="bold">
      <FormattedMessage id="baremetal.firewall.status" />:
     </Typography>
     <FormControlLabel
      control={
       <Checkbox
        checked={enableFirewall}
        onChange={(e, checked) => {
         setEnableFirewall(checked);
        }}
       />
      }
      label={intl.formatMessage({ id: "baremetal.firewall.enableFirewall" })}
     />
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center">
     <Typography variant="caption" fontWeight="bold">
      <FormattedMessage id="baremetal.firewall.whitelistHos" />:
     </Typography>
     <FormControlLabel
      control={
       <Checkbox
        checked={enableHos}
        onChange={(e, checked) => {
         setEnableHos(checked);
        }}
       />
      }
      label={intl.formatMessage({ id: "baremetal.firewall.enableHos" })}
     />
    </Stack>
   </Stack>
   <Stack>
    <Stack textAlign="left">
     <Typography variant="unboundedTitle" fontWeight={16}>
      <FormattedMessage id="baremetal.firewall.inputRules" />
     </Typography>
    </Stack>
    <Divider />
    <Stack spacing={1} p={2} sx={{ overflowX: "auto" }}>
     {inputRules.map((element, index) => {
      if (ruleToEdit === index) {
       return (
        <EditingRule
         key={`input-edit-${index}`}
         rule={element}
         confirmDelete={() => handleDeleteRule("input")}
         confirmEdit={(rule: IFirewallRuleData) => handleAddEditedRule("input", rule)}
        />
       );
      } else {
       return (
        <RuleToEdit
         rule={element}
         key={`input-rule-${index}`}
         disabledEdit={ruleToEdit !== -1}
         editSelectedRule={() => selectRuleToEdit("input", index)}
        />
       );
      }
     })}
     <Button disabled={ruleToEdit !== -1} onClick={() => handleAddRule("input")}>
      <FormattedMessage id="baremetal.firewall.addRule" />
     </Button>
    </Stack>
   </Stack>
   <Stack>
    <Stack textAlign="left">
     <Typography variant="unboundedTitle" fontWeight={16}>
      <FormattedMessage id="baremetal.firewall.outputRules" />
     </Typography>
    </Stack>
    <Divider />
    <Stack spacing={1} p={2} sx={{ overflowX: "auto" }}>
     {outputRules.map((element, index) => {
      if (outputRuleToEdit === index) {
       return (
        <EditingRule
         key={`output-edit-${index}`}
         rule={element}
         confirmDelete={() => handleDeleteRule("output")}
         confirmEdit={(rule: IFirewallRuleData) => handleAddEditedRule("output", rule)}
        />
       );
      } else {
       return (
        <RuleToEdit
         rule={element}
         key={`output-rule-${index}`}
         disabledEdit={outputRuleToEdit !== -1}
         editSelectedRule={() => selectRuleToEdit("output", index)}
        />
       );
      }
     })}
     <Button disabled={outputRuleToEdit !== -1} onClick={() => handleAddRule("output")}>
      <FormattedMessage id="baremetal.firewall.addRule" />
     </Button>
    </Stack>
   </Stack>
   <Stack direction="row" justifyContent="flex-end">
    <Button onClick={closeEdit}>
     <FormattedMessage id="app.back" />
    </Button>
    <Button onClick={handleOpen}>
     <FormattedMessage id="app.confirm" />
    </Button>
    <AppModal
     open={open}
     close={handleClose}
     title={intl.formatMessage({ id: "baremetal.firewall.editFirewall" })}
     handleClose={handleClose}
     handleConfirm={handleConfirm}
     disabled={loading}>
     <Typography>
      <FormattedMessage id="baremetal.firewall.editFirewallMessage" />
     </Typography>
    </AppModal>
   </Stack>
  </Stack>
 );
};

export default EditFirewallConfiguration;
